import React, { useState } from "react";
import styled from "styled-components";
import axios from "axios";

const API_BASE_URL = "https://admin.shopaholics.ge/api";

/* ===== THEME ===== */
const UI = {
  cardBg: "#ffffff",
  pageBg: "#f5f7fb",
  text: "#0f172a",
  dim: "#64748b",
  line: "rgba(2,6,23,.08)",
  focus: "#2563eb",
  primary: "#0ea5e9",
  primaryHover: "#0284c7",
  radius: "14px",
  shadow: "0 10px 28px rgba(2,6,23,.06)",
};

const Page = styled.div`
  min-height: 100vh;
  background: ${UI.pageBg};
  padding: clamp(24px, 4vw, 48px) 16px;
`;

const Container = styled.div`
  max-width: 820px;
  margin-inline: auto;
  background: ${UI.cardBg};
  border: 1px solid ${UI.line};
  border-radius: ${UI.radius};
  box-shadow: ${UI.shadow};
  padding: clamp(20px, 4vw, 28px);
`;

const Heading = styled.h2`
  font-size: clamp(20px, 2.4vw, 24px);
  font-weight: 800;
  letter-spacing: 0.2px;
  color: ${UI.text};
  margin: 2px 0 16px;
`;

const Sub = styled.p`
  margin-top: -6px;
  font-size: 14px;
  color: ${UI.dim};
`;

const Form = styled.form`
  display: grid;
  gap: 14px;
  margin-top: 18px;
`;

const Field = styled.div`
  display: grid; gap: 8px;
`;

const Label = styled.label`
  font-weight: 700;
  color: ${UI.text};
  font-size: 14px;
`;

const Input = styled.input`
  padding: 12px 14px;
  font-size: 15px;
  background: #fff;
  border: 1px solid ${UI.line};
  border-radius: 12px;
  outline: none;
  transition: box-shadow .15s ease, border-color .15s ease, background .15s ease;

  &:focus {
    border-color: ${UI.focus};
    box-shadow: 0 0 0 3px rgba(37,99,235,.15);
  }
`;

const Textarea = styled.textarea`
  padding: 12px 14px;
  min-height: 120px;
  font-size: 15px;
  background: #fff;
  border: 1px solid ${UI.line};
  border-radius: 12px;
  outline: none;
  transition: box-shadow .15s ease, border-color .15s ease;

  &:focus {
    border-color: ${UI.focus};
    box-shadow: 0 0 0 3px rgba(37,99,235,.15);
  }
`;

const Help = styled.div`
  font-size: 12.5px;
  color: ${UI.dim};
  margin-top: 6px;
`;

const UploadCard = styled.div`
  border: 1px dashed ${UI.line};
  border-radius: 12px;
  padding: 14px;
  background: #fcfdff;
`;

const ImagePreview = styled.img`
  width: 180px;
  height: 180px;
  object-fit: cover;
  border-radius: 12px;
  margin-top: 10px;
  border: 1px solid ${UI.line};
  background: #fff;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const ButtonBar = styled.div`
  display: flex; gap: 10px; justify-content: flex-end; margin-top: 6px;
`;

const Button = styled.button`
  appearance: none;
  border: 1px solid transparent;
  border-radius: 12px;
  padding: 12px 16px;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  transition: transform .12s ease, background .15s ease, border-color .15s ease;

  ${({ variant = "primary" }) =>
    variant === "primary"
      ? `
      color: #fff;
      background: ${UI.primary};
      &:hover{ background: ${UI.primaryHover}; }
      &:active{ transform: translateY(1px); }
    `
      : `
      color: ${UI.text};
      background: #f3f6fb;
      border-color: ${UI.line};
      &:hover{ background:#eaf0f9; }
      &:active{ transform: translateY(1px); }
    `}
`;

const Message = styled.div`
  margin: 8px 0 4px;
  font-weight: 600;
  color: ${({ ok }) => (ok ? "#16a34a" : "#dc2626")};
`;

const AddBrand = () => {
  const [brand, setBrand] = useState({
    name: "",
    description_ge: "",
    description_en: "",
  });

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);

  const [background, setBackground] = useState(null);
  const [bgPreview, setBgPreview] = useState(null);

  const [message, setMessage] = useState("");

  const handleChange = (e) => {
    setBrand({ ...brand, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    setImage(file || null);
    setPreview(file ? URL.createObjectURL(file) : null);
  };

  const handleBackgroundChange = (e) => {
    const file = e.target.files?.[0];
    setBackground(file || null);
    setBgPreview(file ? URL.createObjectURL(file) : null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    const formData = new FormData();
    formData.append("name", brand.name);
    formData.append("description_ge", brand.description_ge);
    formData.append("description_en", brand.description_en);
    if (image) formData.append("image", image);
    if (background) formData.append("background", background);

    try {
      const response = await axios.post(`${API_BASE_URL}/add_brand.php`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data?.error) {
        setMessage(response.data.error);
      } else {
        setMessage("Brand added successfully!");
        setBrand({ name: "", description_ge: "", description_en: "" });
        setImage(null); setPreview(null);
        setBackground(null); setBgPreview(null);
      }
    } catch (error) {
      console.error("Error:", error);
      setMessage("Error adding brand");
    }
  };

  const ok = message.toLowerCase().includes("success");

  return (
    <Page>
      <Container>
        <Heading>Add Brand</Heading>
        <Sub>Create a brand, upload logo & optional background for brand pages.</Sub>

        {message && <Message ok={ok}>{message}</Message>}

        <Form onSubmit={handleSubmit}>
          <Field>
            <Label htmlFor="brand-name">Brand Name</Label>
            <Input id="brand-name" type="text" name="name" value={brand.name} onChange={handleChange} required placeholder="e.g. Creed" />
          </Field>

          <Row>
            <Field>
              <Label htmlFor="desc-ge">Description (Georgian)</Label>
              <Textarea id="desc-ge" name="description_ge" value={brand.description_ge} onChange={handleChange} required />
            </Field>
            <Field>
              <Label htmlFor="desc-en">Description (English)</Label>
              <Textarea id="desc-en" name="description_en" value={brand.description_en} onChange={handleChange} required />
            </Field>
          </Row>

          <Row>
            <Field>
              <Label>Brand Logo</Label>
              <UploadCard>
                <Input type="file" accept="image/*" onChange={handleImageChange} required />
                <Help>Recommended: square (e.g. 800×800), PNG (transparent BG).</Help>
                {preview && <ImagePreview src={preview} alt="Brand Logo Preview" />}
              </UploadCard>
            </Field>

            <Field>
              <Label>Brand Background (optional)</Label>
              <UploadCard>
                <Input type="file" accept="image/*" onChange={handleBackgroundChange} />
                <Help>Recommended: wide (e.g. 1600×600), JPG/WEBP for brand header.</Help>
                {bgPreview && <ImagePreview src={bgPreview} alt="Brand Background Preview" />}
              </UploadCard>
            </Field>
          </Row>

          <ButtonBar>
            <Button type="button" variant="secondary" onClick={()=>{
              setBrand({ name:"", description_ge:"", description_en:"" });
              setImage(null); setPreview(null);
              setBackground(null); setBgPreview(null);
              setMessage("");
            }}>Clear</Button>
            <Button type="submit">Add Brand</Button>
          </ButtonBar>
        </Form>
      </Container>
    </Page>
  );
};

export default AddBrand;
